import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useRef, useState, type ElementType, type KeyboardEvent, type ReactNode } from "react";
import { isClippedTableText } from "_shared/_views/tables/table-cell-tooltip";

const clippedText = {
  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
} as const;

// The tooltip's own text, where a title carrying line breaks keeps them.
const revealedTextStyles = { sx: { whiteSpace: "pre-line" } } as const;

type TruncatedCellProps = {
  children: ReactNode;
  /**
   * What the hover reveals, where the text as rendered is not the whole of it: a cell built from
   * several parts draws them as separate lines and its text runs them together, so pass them with
   * their breaks restored. A `\n` becomes a line break in the tooltip. Blank or absent — which is
   * the ordinary case, and lets a caller pass "the parts, if it has any" without branching — falls
   * back to the cell's own text.
   */
  title?: string;
  /** The element to render, where a `div` would be wrong — `time` for a date, `span` inside a `p`. */
  component?: ElementType;
  /**
   * Whether a keyboard can reveal the text too: the cell joins the tab order and shows the whole of
   * itself while focused. For a value the row offers no other way to read, such as an invitation's
   * message where the row opens nothing. Off by default, since every other cell is a tab stop nobody
   * needs.
   */
  focusable?: boolean;
};

/**
 * Table-cell text that ends in an ellipsis when it does not fit, and reveals the whole of itself on
 * hover when — and only when — it was actually clipped (GitHub issue #5130).
 *
 * The ellipsis is the only thing that says a value is longer than its column, and on its own it
 * leaves the reader with no way to read the rest short of widening the window: the communications
 * table's Description column clips most rows at a screen width where the row itself still reads
 * fine. So the tooltip is the other half of the ellipsis, not decoration.
 *
 * A `focusable` cell opens on keyboard focus as well, and Escape closes it.
 *
 * It opens only on measured overflow. A tooltip on every cell would cover the neighbouring rows
 * while saying what the reader can already see, so the check is made at hover rather than at render:
 * `scrollWidth`/`scrollHeight` are the laid-out sizes and asking for them at the moment of the hover
 * is what keeps the answer right through a resized window, a collapsed side pane, or a page the
 * reader has zoomed, none of which re-render the cell and none of which should need an observer of
 * its own to notice.
 *
 * `FeeScheduleTableCellText` does the same for the fee-schedule tables. It stays separate because
 * those cells are `Typography`, and their surrounding styles reach in by the `MuiTypography-root`
 * class that a `Box` would not carry.
 */
export default function TruncatedCell(props: Readonly<TruncatedCellProps>) {
  const cellRef = useRef<HTMLElement>(null);
  // Empty means no tooltip: MUI treats an empty title as nothing to show, so the closed cell also
  // stops lending its text to the child as an `aria-label`.
  const [revealedText, setRevealedText] = useState("");

  const revealIfClipped = () => {
    const cell = cellRef.current;
    if (!cell) return;

    const isClipped = cell.scrollWidth > cell.clientWidth || cell.scrollHeight > cell.clientHeight
      || Array.from(cell.querySelectorAll<HTMLElement>("*")).some(isClippedTableText);
    const fullText = props.title?.trim() ? props.title : (cell.textContent ?? "");
    setRevealedText(isClipped ? fullText : "");
  };

  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") setRevealedText("");
  };

  return (
    <Tooltip
      open={revealedText !== ""}
      title={revealedText}
      onClose={() => setRevealedText("")}
      disableFocusListener
      disableTouchListener
      slotProps={{ tooltip: revealedTextStyles }}
    >
      <Box
        ref={cellRef}
        data-table-cell-tooltip
        component={props.component ?? "div"}
        sx={clippedText}
        onMouseEnter={revealIfClipped}
        onMouseLeave={() => setRevealedText("")}
        tabIndex={props.focusable ? 0 : undefined}
        onFocus={props.focusable ? revealIfClipped : undefined}
        onBlur={props.focusable ? () => setRevealedText("") : undefined}
        onKeyDown={props.focusable ? closeOnEscape : undefined}
      >
        {props.children}
      </Box>
    </Tooltip>
  );
}
